import { AmbientBackpressure } from "./ambient-tracker.js";
import type { TurnRecord } from "./insight-proposer.js";

/**
 * Minimal shape of a recall hit the ambient recaller consumes.
 *
 * Defined locally — `proactive-curator` does not depend on
 * `@vortex-os/memory-extended`. The host injects a {@link RecallFn} whose
 * result structurally satisfies this shape (memory-extended's `RecallHit`
 * carries all of these fields plus a few extra, so the wiring is
 * zero-friction). This mirrors how `LLMJudge` keeps the host's LLM facility
 * behind an injected adapter: the recall *engine* lives in another package;
 * the *decision of when to surface a hit in conversation* lives here.
 */
export interface AmbientRecallHit {
  readonly id: string;
  /** Cosine similarity to the query (1 = identical direction). */
  readonly score: number;
  readonly name: string;
  /** Short body excerpt for the host to weave into prose. */
  readonly excerpt: string;
  /** Corpus the hit came from (e.g. "memory", "session-archive"). */
  readonly source: string;
  readonly type: string;
  readonly updated: string | null;
  readonly tags: readonly string[];
}

/**
 * Host-injected recall function. The host wires its `memory-extended` recall
 * engine (sqlite + vector + embedder + session chunks) behind this single
 * call so the recaller stays engine-agnostic and `proactive-curator` stays
 * free of any `memory-extended` dependency.
 */
export interface RecallFn {
  (query: string, opts?: { readonly k?: number }): Promise<{
    readonly hits: readonly AmbientRecallHit[];
  }>;
}

/** One surfaced suggestion — a recall hit that passed the ambient gate. */
export interface RecallSuggestion {
  readonly hit: AmbientRecallHit;
  /** Convenience copy of `hit.score` (the gate's ranking key). */
  readonly score: number;
}

/**
 * Result of one {@link AmbientRecaller.consider} call. Carries the surviving
 * suggestion(s) plus diagnostics (how many hits the engine returned, how many
 * each gate dropped) so an instance can tune `minScore` against real
 * conversations rather than guessing.
 */
export interface AmbientRecallResult {
  /** Hits that passed every gate, best-first, capped at `maxSuggestions`. */
  readonly suggestions: readonly RecallSuggestion[];
  /** True when backpressure is active — the recaller stayed silent. */
  readonly suppressed: boolean;
  /** The query actually used (derived or explicit); null if none/empty. */
  readonly query: string | null;
  /** Hits returned by the engine before filtering (transparency for tuning). */
  readonly considered: number;
  /** Hits dropped for scoring below `minScore`. */
  readonly belowThreshold: number;
  /** Hits dropped as already surfaced earlier this session. */
  readonly deduped: number;
}

export interface AmbientRecallerOptions {
  readonly recall: RecallFn;
  /**
   * Minimum cosine score for a hit to be worth surfacing unprompted. The
   * single most important tuning knob — too low surfaces noise (naggy), too
   * high never fires. The right value depends on the embedder; calibrate per
   * instance against real conversations (the result diagnostics help). The
   * default 0.5 is a conservative starting point for the bundled e5-small
   * embedder.
   */
  readonly minScore?: number;
  /** Max suggestions per `consider()`. Default 1 — ambient is one nudge, not a list. */
  readonly maxSuggestions?: number;
  /** Below this query length, do not even call the engine. Default 12. */
  readonly minQueryChars?: number;
  /**
   * How many candidates to ask the engine for. Defaults to
   * `maxSuggestions + 4` so dedup/threshold filtering has headroom.
   */
  readonly candidateK?: number;
  /** Shared backpressure controller. Omit to use a fresh one. */
  readonly backpressure?: AmbientBackpressure;
}

const DEFAULT_MIN_SCORE = 0.5;
const DEFAULT_MAX_SUGGESTIONS = 1;
const DEFAULT_MIN_QUERY_CHARS = 12;

/**
 * Decides *whether* and *which* past memory to surface in the flow of a live
 * conversation — the ambient counterpart to the explicit `/recall` command
 * (memory-extended-design.md deferred this "notice and offer" reliability to
 * a proactive-curator-connected follow-up; this is it).
 *
 * It does not embed, search, or format anything itself. It takes the recent
 * conversation, derives a query, asks the injected recall engine, and applies
 * three gates so the surface stays useful and non-naggy:
 *
 *   1. **Backpressure** — after N consecutive declines (see
 *      {@link AmbientBackpressure}) it goes silent for the rest of the session.
 *   2. **Score threshold** — only confident hits surface.
 *   3. **Session dedup** — a hit already offered this session is not re-offered.
 *
 * Stateful by design (the dedup set + backpressure streak live across turns),
 * so a host keeps ONE instance alive for the session — a long-lived runtime
 * or the opt-in embedder daemon. A stateless per-call host (a plain slash
 * command) cannot carry the gate state; that is exactly why the default
 * Claude Code surface is agent-guidance, not a command (see the design docs).
 */
export class AmbientRecaller {
  private readonly recall: RecallFn;
  private readonly minScore: number;
  private readonly maxSuggestions: number;
  private readonly minQueryChars: number;
  private readonly candidateK: number;
  private readonly backpressure: AmbientBackpressure;
  private readonly surfaced = new Set<string>();

  constructor(options: AmbientRecallerOptions) {
    this.recall = options.recall;
    this.minScore = options.minScore ?? DEFAULT_MIN_SCORE;
    this.maxSuggestions = options.maxSuggestions ?? DEFAULT_MAX_SUGGESTIONS;
    this.minQueryChars = options.minQueryChars ?? DEFAULT_MIN_QUERY_CHARS;
    this.candidateK = options.candidateK ?? this.maxSuggestions + 4;
    this.backpressure = options.backpressure ?? new AmbientBackpressure();
  }

  /**
   * Consider surfacing a memory given the current conversation. Pass an
   * explicit `query` (the host knows what the user is referencing) or recent
   * `turns` to derive one from. Returns the gated suggestions plus diagnostics.
   *
   * Does not catch errors from the injected engine — a failing embedder
   * should surface to the host (which decides whether to swallow it for the
   * turn), not be masked here.
   */
  async consider(input: {
    readonly query?: string;
    readonly turns?: readonly TurnRecord[];
  }): Promise<AmbientRecallResult> {
    if (this.backpressure.isActive()) {
      return emptyResult(true, null);
    }
    const query = (input.query ?? deriveQueryFromTurns(input.turns ?? [])).trim();
    if (query.length < this.minQueryChars) {
      return emptyResult(false, query.length === 0 ? null : query);
    }

    const { hits } = await this.recall(query, { k: this.candidateK });
    let belowThreshold = 0;
    let deduped = 0;
    const suggestions: RecallSuggestion[] = [];
    for (const hit of hits) {
      if (hit.score < this.minScore) {
        belowThreshold++;
        continue;
      }
      if (this.surfaced.has(hit.id)) {
        deduped++;
        continue;
      }
      suggestions.push({ hit, score: hit.score });
      if (suggestions.length >= this.maxSuggestions) break;
    }
    for (const s of suggestions) this.surfaced.add(s.hit.id);

    return {
      suggestions,
      suppressed: false,
      query,
      considered: hits.length,
      belowThreshold,
      deduped,
    };
  }

  /** The user engaged with a surfaced suggestion — clears the decline streak. */
  recordAccept(): void {
    this.backpressure.recordAccept();
  }

  /** The user dismissed a surfaced suggestion — counts toward backpressure. */
  recordDecline(): void {
    this.backpressure.recordDecline();
  }

  /** True when backpressure has silenced ambient surfacing for the session. */
  get suppressed(): boolean {
    return this.backpressure.isActive();
  }

  /**
   * Re-engage after backpressure (the user explicitly asks for suggestions
   * again, e.g. via `/curate`). Clears the decline streak AND the dedup set
   * so previously surfaced hits can be offered again.
   */
  reset(): void {
    this.backpressure.reset();
    this.surfaced.clear();
  }
}

/**
 * Build a recall query from recent turns. Uses the most recent *user* turn —
 * in ambient use the trigger is the user's latest utterance ("didn't I do
 * this before?"), and that utterance is the query. Falls back to the most
 * recent turn of any role if the window has no user turn.
 */
export function deriveQueryFromTurns(turns: readonly TurnRecord[]): string {
  for (let i = turns.length - 1; i >= 0; i--) {
    if (turns[i]!.role === "user") return turns[i]!.content;
  }
  return turns.length > 0 ? turns[turns.length - 1]!.content : "";
}

function emptyResult(suppressed: boolean, query: string | null): AmbientRecallResult {
  return { suggestions: [], suppressed, query, considered: 0, belowThreshold: 0, deduped: 0 };
}
